<template>
  <div>
    <div class="search">
      <div class="search__prepend" ref="searchTags">
        <TransitionGroup name="fade">
          <div
            v-for="(tag, i) in queryTerms"
            :key="i"
            class="search__tag">
            <div class="search__tag__type">
              {{ tag.tagTitle }}
            </div>
            <div
              class="search__tag__value"
              v-if="tag.valueTitle || (tag.type === 'created_at' && tag.value)"
              :style="{ 'background-color': tag.color, 'color': tag.textColor }">
              <template v-if="tag.type === 'created_at' && tag.value">
                {{ tag.value[0] | formatDate('medium') }} - {{ tag.value[1] | formatDate('medium') }}
              </template>
              <template v-else>
                {{ tag.valueTitle }}
              </template>
              <XMarkIcon
                data-ec="envelope_search"
                data-et="delete_tag_button"
                class="search__tag__value__remove"
                @click.native="handleRemoveTag(tag)"
                height="10"
                width="10" />
            </div>
          </div>
        </TransitionGroup>
        <ElDatePicker
          ref="datePicker"
          key="datePicker"
          class="date-picker"
          data-ec="envelope_search"
          data-et="date_picker"
          v-model="dateRange"
          @change="setDateRange"
          type="daterange"
          align="left"
          unlink-panels
          :clearable="false"
          :format="dateFormat"
          :default-time="['00:00:00', '23:59:59']"
          :picker-options="pickerOptions">
        </ElDatePicker>
      </div>
      <div class="search__text">
        <ElAutocomplete
          :data-ec="ec"
          data-et="search_input"
          :placeholder="$t('ui.envelopes.envelopesPage.searchPlaceholder')"
          v-model="searchQuery"
          :fetch-suggestions="querySearch"
          @select="handleAddTag"
          @keyup.delete.native="handleDelete"
          @keyup.enter.native="doSearch"
          ref="searchAutocomplete">
          <template slot-scope="{ item }">
            <div v-if="item.type && item.type === 'pill'"
              class="autocomplete_item--pill"
              :style="stylePill(item)">
              {{ item.tagTitle || item.valueTitle }}
            </div>
            <div v-else-if="item.type && item.type === 'date'">

            </div>
            <div v-else>
              {{ item.tagTitle || item.valueTitle }}
            </div>
          </template>
          <template v-slot:suffix>
            <i
              class="search__text__clear el-input__icon el-icon-circle-close"
              v-if="searchQuery || queryTerms.length"
              @click="clearSearch"
              data-ec="envelope_search"
              data-et="clear_search_button">
            </i>
          </template>
          <template v-slot:append>
            <ElButton
              icon="el-icon-search"
              @click="doSearch"
              data-ec="envelope_search"
              data-et="search_button"
            />
          </template>
        </ElAutocomplete>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import dateFormat from '@/mixins/americanDateSwitcherMixin';
import SearcherMixin from '@/mixins/searcherMixin';
import useList from '@/composables/list/list';
import { BulkSend } from '@/store/modules/bulkSend/models';
import { XMarkIcon } from '@/components/icons/fontIcons';

export default {
  name: 'VEnvelopeSearch',
  components: {
    XMarkIcon,
  },
  props: {
    ec: {
      type: String,
      required: false,
    },
    initialQuery: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  setup() {
    const [fetchBulkSends, bulkSendsList] = useList('bulk-sends', BulkSend);

    return {
      fetchBulkSends,
      bulkSendsList,
    };
  },
  created() {
    this.handleInitialQuery();
  },
  mixins: [dateFormat, SearcherMixin('contacts'), SearcherMixin('users')],
  data() {
    return {
      pickerOptions: {
        disabledDate: (d) => {
           const endOfDay = new Date().setHours(23, 59, 59);
           if (d > endOfDay) return true;
           return false;
        },
      },
      dateRange: null,
      searchQuery: '',
      queryTerms: [], // Holds the current list of filter terms as objects
      queryFilters: {}, // Applied search filters
      tagTypes: [ // Available query tag types
        {
          tagTitle: this.$t('functional.dateCreated'),
          value: 'Date Created',
          type: 'pill',
          tag: 'created_at',
        },
        {
          tagTitle: this.$t('functional.sentBy'),
          value: 'Sent By',
          type: 'pill',
          tag: 'sender_id',
        },
        {
          tagTitle: this.$t('functional.recipient'),
          value: 'Recipient',
          type: 'pill',
          tag: 'contact_id',
        },
        {
          tagTitle: this.$t('functional.bulkSend'),
          value: 'Bulk Send',
          type: 'pill',
          tag: 'bulk_send_fingerprint',
        },
      ],
    };
  },
  methods: {
    stylePill(item) {
      const styles = {
        'background-color': item.color,
      };
      if (item.isOption) {
        styles.color = item.textColor;
      }
      return styles;
    },
    setDateRange() {
      const unixDateRange = this.dateRange.map(d => d.getTime() / 1000);
      this.handleAddTag({
        color: '#404040',
        value: unixDateRange,
      });
    },
    /**
     * Handle deleting query terms via backspace
     */
    handleDelete() {
      if (!this.searchQuery.length && this.queryTerms.length) this.queryTerms.pop();
      if (!this.searchQuery.length) {
        this.cancelContactsSearch();
      }
    },
    doSearch() {
      if (this.queryTerms.length && !this.queryTerms[this.queryTerms.length - 1].value) this.queryTerms.pop(); // Remove any incomplete filters from the end
      this.$emit('onSearch', { ...this.queryFilters, query: this.searchQuery });
      this.$refs.searchAutocomplete.$el.blur();
    },
    clearSearch() {
      this.searchQuery = '';
      this.queryFilters = {};
      this.queryTerms = [];
      this.doSearch();
    },
    /**
     * Build the list of dropdown options
     * Has two states:
     * - the start of a query - type
     * - the param of a query - value
     * @param {String} queryString
     * @param {Function} cb callback function
     */
    querySearch(queryString, cb) {
      // Check if we've got an existing tag addition.
      const currentTag = this.queryTerms[this.queryTerms.length - 1];
      if (currentTag && !currentTag.value) { // We're already building a search.
        return this.buildTagValueResults(currentTag, cb, this.searchQuery);
      }
      return this.buildTagTypeResults(this.searchQuery, cb);
    },

    /**
     * Build the list of dropdown values for a specific tag type
     * @param {Object} currentTag
     * @param {Function} cb callback function
     */
    async buildTagValueResults(currentTag, cb, query) { // TODO Simplify, make universal
      switch (currentTag.type) {
        case 'sender_id': {
          await this.searchUsers({ query });
          let users = this.usersSearchResults.map(user => ({ valueTitle: user.name, value: user.id, color: '#3A4657' }));
          // Filter out users already included in search
          users = users.filter(state => !this.queryFilters.sender_id.inc.find(id => id === state.value));
          cb(users);
          break;
        }
        case 'contact_id': {
          await this.searchContacts({ query });
          let contacts = this.contactsSearchResults.map(contact => ({ valueTitle: contact.title, value: contact.id, color: '#573259' }));
          // Filter out users already included in search
          contacts = contacts.filter(state => !this.queryFilters.contact_id.inc.find(id => id === state.value));
          cb(contacts);
          break;
        }
        case 'bulk_send_fingerprint': {
          await this.fetchBulkSends({ query, status: { inc: ['complete'] } });
          const bulkSends = this.bulkSendsList
            .map(bulkSend => ({ valueTitle: bulkSend.name, value: bulkSend.fingerprint }))
            .filter(item => !this.queryFilters.bulk_send_fingerprint.inc.includes(item.value));
          cb(bulkSends);
          break;
        }
        case 'created_at': { // Allow date range input
          this.$refs.searchAutocomplete.$el.blur();
          this.dateRange = null;
          cb(null);
          this.$refs.datePicker.focus();
          break;
        }
        default:
          cb([]);
      }
    },

    /**
     * Build the list of dropdown types
     * @param {String} queryString
     * @param {Function} cb callback function
     */
    buildTagTypeResults(queryString, cb) {
      let results = this.tagTypes;
      // remove created date from dropdown if already used
      results = this.queryFilters.created_at?.inc?.length ? results.filter(tag => tag.tag !== 'created_at') : results;
      results = queryString ? results.filter(tag => tag.tagTitle.toLowerCase().startsWith(queryString.toLowerCase())) : results;
      // results = queryString ? results.filter(this.filterBuilder(queryString)) : results; // Autocomplete filter based on query string
      // call callback function to return suggestions
      cb(results);
    },

    /**
     * Partial application to build a case insensitive filter
     * @param {String} queryString
     * @param {Function} cb callback function
     */
    filterBuilder(queryString) {
      return link => (link.value.toLowerCase().indexOf(queryString.toLowerCase()) === 0);
    },
    /**
     * Adding a new tag to the query terms collection
     * If a partial tag exists, add to it.
     * If not, add a new tag.
     */
    handleAddTag(item) {
      this.searchQuery = ''; // Blank after adding
      if (item.tag && !this.queryFilters[item.tag]) {
        this.queryFilters[item.tag] = {};
        this.queryFilters[item.tag].inc = [];
      }
      // this.queryFilters[item.tag].push(item.value);

      let currentTag = this.queryTerms[this.queryTerms.length - 1];

      if (currentTag && !currentTag.value) {
        currentTag.value = item.value;
        currentTag.valueTitle = item.valueTitle;
        currentTag.color = item.color;
        currentTag.textColor = item.textColor;
        this.queryFilters[currentTag.type].inc.push(item.value);
      } else {
        this.queryTerms.push({
          tagTitle: item.tagTitle,
          valueTitle: item.valueTitle,
          type: item.tag,
          value: '',
        });
      }

      currentTag = this.queryTerms[this.queryTerms.length - 1];
      if (currentTag && currentTag.value) this.doSearch();

      this.$nextTick(() => {
        this.$refs.searchTags.scrollLeft = this.$refs.searchTags.scrollWidth;
        this.$refs.searchAutocomplete.focus();
      });
    },
    /**
     * Remove a tag from the collection when clicking 'x'
     */
    handleRemoveTag(tag) {
      if (Object.prototype.hasOwnProperty.call(this.$route.query, tag.type)) {
        const query = Object.assign({}, this.$route.query);
        delete query[tag.type];

        if (tag.type === 'bulk_send_fingerprint') {
          delete query.bulk_send_name;
        }

        this.$router.replace({ query });
      }

      this.queryFilters[tag.type].inc.splice(this.queryFilters[tag.type].inc.indexOf(tag.value), 1);
      this.queryTerms.splice(this.queryTerms.indexOf(tag), 1);
      this.doSearch();
    },
    handleInitialQuery() {
      Object.keys(this.initialQuery).forEach((key) => {
        switch (key) {
          case 'bulk_send_fingerprint':
            this.queryTerms.push({
              tagTitle: this.$t('functional.bulkSend'),
              valueTitle: this.initialQuery.bulk_send_name,
              type: 'bulk_send_fingerprint',
              value: this.initialQuery.bulk_send_fingerprint,
            });

            this.queryFilters.bulk_send_fingerprint = {
              inc: [this.initialQuery.bulk_send_fingerprint],
            };

            break;
          default:
            // Do nothing
        }
      });
    },
  },
  computed: {
    ...mapGetters({
      usersLoaded: 'users/hasLoaded',
      contactsLoaded: 'contacts/hasLoaded',
    }),
    showDateInput: {
      get() {
        return this.queryTerms.length && // Has query terms
        this.queryTerms[this.queryTerms.length - 1].type === 'date' && // Latest has type of date
        !this.queryTerms[this.queryTerms.length - 1].value; // And no value
      },
    },
  },
};
</script>

<style>
.date-picker {
  width: 0 !important;
  padding: 0 !important;
  border: none !important;
  position: absolute !important;
  margin: 0;
  height: 16px;
  overflow: hidden;
}

.search .el-input {
  transition: 0.2s ease all;
}

.search .el-input input {
  border: none;
  min-width: 200px;
  padding-left: 5px;
  height: 38px;
}

.search .el-input input:focus {
  outline: none;
  box-shadow: none !important;
  border: none !important;
}

.search .el-input-group__prepend {
  background-color: white !important;
  padding: 0 !important;
  padding-left: 10px !important;
  border: none !important;
}

.el-input-group__append {
  border: none !important;
  border-left: 1px solid var(--color-border) !important;
  padding: 0 10px !important;
  background: #fefefe !important;
  color: grey !important;
}

.el-tag {
  margin: 0 3px;
  font-size: 11px;
}

.el-input-group__prepend .el-tag:first-of-type {
  margin-left: 10px;
}

.el-input-group__prepend .el-tag + .el-input-group__prepend .el-tag {
  margin-right: 10px;
}

.el-autocomplete {
  width: 100%;
}

.el-autocomplete-suggestion {
  width: 180px !important;
}

.autocomplete_item--pill {
  color: white;
  background: #6A6E81;
  border-radius: 15px;
  margin: 5px 0;
  padding: 0 10px;
  display: inline-block;
  font-size: 11px;
  line-height: 25px;
}

.search {
  height: 40px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: white;
  display: flex;
  overflow: hidden;
}

.search:focus-within {
  border-color: var(--signable-secondary-2-shade-3);
  border-width: 1px;
  -webkit-box-shadow: 0px 0px 0px 2px rgba(141, 173, 192, 0.25);
  box-shadow: 0px 0px 0px 2px rgba(141, 173, 192, 0.25);
}

.search:focus-within .el-input-group__append {
  background: #0098ce !important;
  color: #ffffff !important;
  border-left: none !important;
  border-top-right-radius: 2px !important;
  border-bottom-right-radius: 2px !important;
}

.search:focus-within .search__text {
  margin-right: -1px;
}

.search__prepend {
  background: none;
  display: inline-block;
  padding: 8px;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
}

.search__text {
  flex-grow: 1;
  min-width: 247px;
}

.search__text .el-dropdown, .search__text .el-dropdown-link
input {
  color: #606266;
  width: 100%;
  height: 100%;
  border: none;
  padding: 0 8px;
  background: none;
}

.search__text ::placeholder {
  color: #bebebe;
}

.search__text__clear {
  cursor: pointer;
}

.search__tag {
  display: inline-block;
  height: 23px;
  font-size: 11px;
  margin: 0;
  margin-left: 5px;
}

.search__tag:nth-of-type(1) {
  margin-left: 0;
}

.search__tag div {
  display: inline-block;
  line-height: 23px;
  padding: 0 8px;
  text-transform: capitalize;
}

.search__tag__type {
  background: #6A6E81;
  color: white;
  border-top-left-radius: 15px;
  border-bottom-left-radius: 15px;
  margin-right: 1px;
}

.search__tag__value {
  color: white;
  background: #0098CE;
  border-top-right-radius: 15px;
  border-bottom-right-radius: 15px;
}

.search__tag__value__remove {
  cursor: pointer;
  margin-top: 1px;
  vertical-align: text-top;
}
</style>
